/**
 * Product Add to Cart Tracker для Google Analytics Enhanced Ecommerce
 * Відстежує додавання товарів в кошик та відправляє подію add_to_cart
 */

class ProductAddToCartTracker {
    constructor() {
        this.addedProducts = new Set(); // Зберігає ID товарів, які вже були додані в кошик
        this.lastSentAtByProductId = new Map(); // Антидублювання за часом
        // Додаємо унікальний ID для цього екземпляра
        this.instanceId = Math.random().toString(36).substr(2, 9);
        // Глобальний реєстр для запобігання дублюванню між різними трекерами
        if (!window.__globalAddToCartTracker) {
            window.__globalAddToCartTracker = {
                sentEvents: new Set(),
                lastSentAt: new Map(),
                instances: new Set(),
                activeInstance: null
            };
        }
        this.globalTracker = window.__globalAddToCartTracker;
        this.globalTracker.instances.add(this.instanceId);
        // Якщо це перший екземпляр або активного екземпляра немає, робимо цей активним
        if (!this.globalTracker.activeInstance) {
            this.globalTracker.activeInstance = this.instanceId;
            this.init();
        }
    }
    init() {
        // Ініціалізуємо слухачі подій
        this.setupClickHandlers();
    }
    /**
     * Налаштовує обробники кліків для кнопок додавання в кошик
     */
    setupClickHandlers() {
        // Слухаємо всі кліки на сторінці
        document.addEventListener('click', (event) => {
            this.handleClick(event);
        }, true);
    }
    /**
     * Обробляє кліки на сторінці
     * @param {Event} event - Подія кліку
     */
    handleClick(event) {
        // Перевіряємо, чи це активний екземпляр
        if (this.globalTracker.activeInstance !== this.instanceId) {
            return;
        }
        const target = event.target;
        
        // Специальное логирование для buy-button
        if (target.classList.contains('buy-button')) {
        }
        
        // Перевіряємо, чи це кнопка додавання в кошик
        if (this.isAddToCartButton(target)) {
            this.trackAddToCart(target, event);
        }
    }
    /**
     * Перевіряє, чи є елемент кнопкою додавання в кошик
     * @param {Element} element - Елемент для перевірки
     * @returns {boolean}
     */
    isAddToCartButton(element) {
        // Спочатку перевіряємо виключення - кнопки, які НЕ повинні відстежуватися
        if (this.shouldExcludeButton(element)) {
            return false;
        }
        // Перевіряємо по тексту кнопки (більш точні умови)
        const buttonText = (element.textContent || element.innerText || '').trim();
        const addToCartTexts = [
            'В кошик',
            'Додати в кошик',
            'В корзину',
            'Добавить в корзину',
            'Add to cart'
        ];
        // Перевіряємо точне співпадіння або початок тексту
        const hasAddToCartText = addToCartTexts.some(text => {
            const lowerButtonText = buttonText.toLowerCase();
            const lowerText = text.toLowerCase();
            return lowerButtonText === lowerText || 
                   lowerButtonText.startsWith(lowerText) ||
                   (lowerButtonText.includes(lowerText) && lowerButtonText.length < 50);
        });
        if (hasAddToCartText) {
            return true;
        }
        // Перевіряємо по класах (більш специфічні умови)
        const classList = element.classList;
        const hasSpecificClass = classList.contains('add-to-cart') ||
                                classList.contains('basket-button') ||
                                classList.contains('buy-button') ||
                                (classList.contains('mainButton') && this.isInProductContext(element) && this.hasAddToCartText(element));
        if (hasSpecificClass) {
            return true;
        }
        // Перевіряємо по контексту (кнопка повинна бути в контексті товару)
        if (this.isInProductContext(element)) {
            // Перевіряємо по SVG іконці кошика
            const svgIcon = element.querySelector('svg, img[src*="basket"], img[src*="cart"]');
            if (svgIcon) {
                return true;
            }
            // Перевіряємо по data-атрибутах
            if (element.hasAttribute('data-add-to-cart') ||
                element.hasAttribute('data-basket-action') ||
                element.hasAttribute('data-track-add-to-cart')) {
                return true;
            }
        }
        return false;
    }
    /**
     * Перевіряє, чи містить кнопка текст додавання в кошик
     * @param {Element} element - Елемент для перевірки
     * @returns {boolean}
     */
    hasAddToCartText(element) {
        const buttonText = (element.textContent || element.innerText || '').trim().toLowerCase();
        const addToCartTexts = [
            'в кошик',
            'додати в кошик',
            'в корзину',
            'добавить в корзину',
            'add to cart'
        ];
        return addToCartTexts.some(text => {
            return buttonText === text || 
                   buttonText.startsWith(text) ||
                   (buttonText.includes(text) && buttonText.length < 50);
        });
    }
    /**
     * Перевіряє, чи потрібно виключити кнопку з відстеження
     * @param {Element} element - Елемент для перевірки
     * @returns {boolean}
     */
    shouldExcludeButton(element) {
        // Перевіряємо, чи це кнопка "Купити в один клік"
        if (this.isOneClickBuyButton(element)) {
            return true;
        }
        // Виключаємо кнопки в навігації, футері, хедері
        if (element.closest('nav, header, footer, .navigation, .menu, .header, .footer')) {
            return true;
        }
        // Виключаємо кнопки в button__wrapper (загальні кнопки, не додавання в кошик)
        if (element.closest('.button__wrapper')) {
            return true;
        }
        // Виключаємо кнопки в рядку цін картки товару
        if (element.closest('.card__wrapper_buttons-row')) {
            return true;
        }
        // Виключаємо кнопки в модальних вікнах, які не стосуються товарів
        if (element.closest('.modal, .popup') && !element.closest('[data-product-id]')) {
            return true;
        }
        // Виключаємо кнопки з специфічними класами виключення
        const excludeClasses = [
            'close-button', 'close-btn', 'cancel-button', 'cancel-btn',
            'back-button', 'prev-button', 'next-button',
            'search-button', 'filter-button', 'sort-button',
            'login-button', 'register-button', 'logout-button',
            'contact-button', 'about-button', 'help-button',
            'button__wrapper', // Обгортка для загальних кнопок
            'card__wrapper_buttons-row' // Рядок цін і кнопки в картці товару
        ];
        if (excludeClasses.some(cls => element.classList.contains(cls))) {
            return true;
        }
        // Виключаємо кнопки з специфічними data-атрибутами (включаючи батьківські елементи)
        if (element.hasAttribute('data-exclude-tracking') ||
            element.hasAttribute('data-no-track') ||
            element.hasAttribute('data-ignore') ||
            element.closest('[data-exclude-tracking]') ||
            element.closest('[data-no-track]') ||
            element.closest('[data-ignore]')) {
            return true;
        }
        // Виключаємо кнопки з текстом, який не стосується додавання в кошик
        const buttonText = (element.textContent || element.innerText || '').toLowerCase();
        const excludeTexts = [
            'закрити', 'скасувати', 'назад', 'далі', 'попередня', 'наступна',
            'пошук', 'фільтр', 'сортування', 'увійти', 'зареєструватися',
            'контакти', 'про нас', 'допомога', 'зв\'язатися', 'зателефонувати',
            'передзвонити', 'замовити', 'перейти', 'дивитися', 'читати',
            'close', 'cancel', 'back', 'next', 'prev', 'search', 'filter',
            'login', 'register', 'contact', 'about', 'help', 'call', 'order',
            'go', 'view', 'read', 'more', 'details'
        ];
        if (excludeTexts.some(text => buttonText.includes(text))) {
            return true;
        }
        return false;
    }
    /**
     * Перевіряє, чи знаходиться кнопка в контексті товару
     * @param {Element} element - Елемент для перевірки
     * @returns {boolean}
     */
    isInProductContext(element) {
        const isBuyButton = element.classList.contains('buy-button');
        
        // Перевіряємо, чи кнопка знаходиться в картці товару
        const productCard = element.closest('.card, .product-card, .products__wrapper_products-product, .products__wrapper-item, [data-product-id]');
        if (productCard) {
            return true;
        }
        // Перевіряємо, чи кнопка знаходиться на сторінці товару
        if (window.location.pathname.includes('/product/')) {
            return true;
        }
        // Перевіряємо, чи кнопка знаходиться в каталозі товарів
        if (window.location.pathname.includes('/catalog') || 
            window.location.pathname.includes('/category')) {
            return true;
        }

        return false;
    }
    /**
     * Перевіряє, чи є елемент кнопкою "Купити в один клік"
     * @param {Element} element - Елемент для перевірки
     * @returns {boolean}
     */
    isOneClickBuyButton(element) {
        // Перевіряємо по тексту кнопки
        const buttonText = element.textContent || element.innerText || '';
        const oneClickTexts = [
            'Купити в один клік',
            'Купить в один клик',
            'Buy in one click',
            'One click buy',
            'Швидке замовлення',
            'Быстрый заказ',
            'Quick order'
        ];
        if (oneClickTexts.some(text => buttonText.toLowerCase().includes(text.toLowerCase()))) {
            return true;
        }
        // Перевіряємо по класах та селекторах
        const classList = element.classList;
        if (classList.contains('main-button') &&
            element.closest('.fast-buy_button')) {
            return true;
        }
        // Перевіряємо по data-атрибутах
        if (element.hasAttribute('data-one-click') ||
            element.hasAttribute('data-fast-order')) {
            return true;
        }
        return false;
    }
    /**
     * Відстежує додавання товару в кошик
     * @param {Element} button - Кнопка додавання в кошик
     * @param {Event} event - Подія кліку
     */
    trackAddToCart(button, event) {
        // Специальное логирование для buy-button
        if (button.classList.contains('buy-button')) {
        }
        
        // Додаткова перевірка - переконаємося, що це дійсно кнопка додавання в кошик
        if (!this.isAddToCartButton(button)) {
            return;
        }
        // Перевіряємо, чи кнопка знаходиться в контексті товару
        if (!this.isInProductContext(button)) {
            return;
        }
        // Знаходимо дані товару
        const productData = this.getProductDataFromButton(button);
        if (!productData) {
            if (button.classList.contains('buy-button')) {
            } else {
            }
            return;
        }

        // Нормалізований ID для контролю дублікатів
        const productId = this.extractProductId(productData);
        const normalizedId = this.normalizeId(productId);
        // Убираем блокировку повторного добавления товара в рамках сессии
        // Пользователь может добавлять один и тот же товар несколько раз
        // Дебаунс за часом: не частіше ніж раз на 1000мс для одного товару
        const now = Date.now();
        const lastAt = this.lastSentAtByProductId.get(normalizedId);
        if (lastAt && now - lastAt < 1000) {
            if (button.classList.contains('buy-button')) {
            } else {
            }
            return;
        }
        this.lastSentAtByProductId.set(normalizedId, now);
        // Убираем добавление товара в Set, чтобы разрешить повторные добавления
        // Відправляємо подію
        if (button.classList.contains('buy-button')) {
        }
        this.sendAddToCartEvent(productData);
    }
    /**
     * Отримує дані товару з кнопки
     * @param {Element} button - Кнопка додавання в кошик
     * @returns {Object|null}
     */
    getProductDataFromButton(button) {
        // Спробуємо знайти картку товару
        const productCard = this.findProductCard(button);
        if (!productCard) {
            console.warn('❌ Product card not found');
            return null;
        }
        // Перевіряємо, чи це DOM елемент чи об'єкт даних
        if (productCard && typeof productCard.getAttribute === 'function') {
            // Це DOM елемент - отримуємо дані з картки товару
            return this.extractProductDataFromCard(productCard);
        } else if (productCard && typeof productCard === 'object') {
            // Це об'єкт даних (з getCurrentProductData)
            return productCard;
        } else {
            console.warn('❌ Unknown product card type:', typeof productCard);
            return null;
        }
    }
    /**
     * Знаходить картку товару
     * @param {Element} button - Кнопка
     * @returns {Element|Object|null} - DOM елемент картки або об'єкт даних товару
     */
    findProductCard(button) {
        // Спочатку шукаємо найближчу картку товару
        const cardElement = button.closest('.card, .product-card, .products__wrapper_products-product, .products__wrapper-item, [data-product-id]');
        if (cardElement) {
            return cardElement;
        }
        // Якщо не знайшли через closest, шукаємо вгору по DOM дереву
        let currentElement = button;
        for (let i = 0; i < 10; i++) {
            if (!currentElement) break;
            // Перевіряємо, чи це картка товару з data-атрибутами
            if (currentElement.hasAttribute('data-product-id')) {
                return currentElement;
            }
            // Перевіряємо, чи це картка товару за класами
            if (currentElement.classList.contains('card') ||
                currentElement.classList.contains('product-card') ||
                currentElement.classList.contains('products__wrapper_products-product') ||
                currentElement.classList.contains('products__wrapper-item')) {
                return currentElement;
            }
            currentElement = currentElement.parentElement;
        }
        // Якщо не знайшли картку, спробуємо отримати дані з поточної сторінки товару
        if (window.location.pathname.includes('/product/')) {
            return this.getCurrentProductData();
        }
        return null;
    }
    /**
     * Витягує дані товару з картки
     * @param {Element} card - Картка товару
     * @returns {Object|null}
     */
    extractProductDataFromCard(card) {
        // Спочатку спробуємо отримати дані з Vue компонента (пріоритет)
        const vueData = this.getVueComponentData(card);
        // Отримуємо базові дані з data-атрибутів (фолбек)
        const productId = card.getAttribute('data-product-id');
        const productTitle = card.getAttribute('data-product-title');
        const productBrandAttr = card.getAttribute('data-product-brand');
        const productCategoryAttr = card.getAttribute('data-product-category');
        // Використовуємо Vue дані якщо доступні, інакше data-атрибути
        // Пріоритет: product_price_id > id
        const rawId = vueData?.product_price_id || vueData?.id || productId;
        if (!rawId) {
            console.warn('❌ Product ID not found in Vue data or data attributes');
            return null;
        }
        // Нормалізуємо ID до числа, якщо можливо
        const numericId = Number(rawId);
        const finalId = (!isNaN(numericId) && isFinite(numericId)) ? numericId : rawId;
        // Витягуємо ціну з Vue даних або з DOM
        let productPrice = vueData?.productPrice;
        if (!productPrice || !productPrice.price || productPrice.price.price === 0) {
            const extractedPrice = this.extractPriceFromCard(card);
            productPrice = {
                price: {price: extractedPrice},
                baseUnit: {isM2: false, title: 'шт'}
            };
        }
        
        // Формуємо об'єкт товару з пріоритетом Vue даних
        const productData = {
            id: finalId,
            product_price_id: vueData?.product_price_id || null, // Зберігаємо product_price_id якщо є
            title: vueData?.title || productTitle || `Товар ${finalId}`,
            productPrice: productPrice,
            category: vueData?.category || (productCategoryAttr ? {name: productCategoryAttr} : this.extractCategoryFromDOM(card)),
            productBrand: vueData?.productBrand || (productBrandAttr ? {title: productBrandAttr} : this.extractBrandFromDOM(card)),
            quantity: this.extractQuantityFromCard(card)
        };
        // Додаткова валідація на рівні витягування даних
        if (!productData.title || productData.title.trim() === '') {
            const titleFromDOM = this.extractTitleFromCard(card);
            if (titleFromDOM) {
                productData.title = titleFromDOM;
            }
        }
        return productData;
    }
    /**
     * Отримує дані з Vue компонента (якщо доступно) - копія з clickTracker
     * @param {HTMLElement} element - Елемент товару
     * @returns {Object|null}
     */
    getVueComponentData(element) {
        // Шукаємо Vue інстанс в елементі
        if (element.__vue__) {
            const vueInstance = element.__vue__;
            // Пріоритет: item, currentProduct, $data
            return vueInstance.$props?.item || 
                   vueInstance.$data?.currentProduct || 
                   vueInstance.$data;
        }
        // Шукаємо в батьківських елементах
        let currentElement = element.parentElement;
        for (let i = 0; i < 5; i++) {
            if (!currentElement) break;
            if (currentElement.__vue__) {
                const vueInstance = currentElement.__vue__;
                // Пріоритет: item, currentProduct, $data
                return vueInstance.$props?.item || 
                       vueInstance.$data?.currentProduct || 
                       vueInstance.$data;
            }
            currentElement = currentElement.parentElement;
        }
        return null;
    }
    /**
     * Витягує ціну з DOM елемента
     * @param {HTMLElement} element - Елемент товару
     * @returns {Object}
     */
    extractPriceFromDOM(element) {
        // Шукаємо ціну в різних селекторах
        const priceSelectors = [
            '.price .price__value',
            '.price-value',
            '.product-price',
            '.card-price',
            '[data-price]',
            '.price',
            '.cost',
            '.amount'
        ];
        
        for (const selector of priceSelectors) {
            const priceElement = element.querySelector(selector);
            if (priceElement) {
                const priceText = priceElement.textContent || priceElement.innerText || '';
                const price = this.parsePriceFromText(priceText);
                if (price > 0) {
                    return {
                        price: {price: price},
                        baseUnit: {isM2: false, title: 'шт'}
                    };
                }
            }
        }
        
        // Шукаємо в data-атрибутах
        const dataPrice = element.getAttribute('data-price');
        if (dataPrice) {
            const price = parseFloat(dataPrice);
            if (!isNaN(price) && price > 0) {
                return {
                    price: {price: price},
                    baseUnit: {isM2: false, title: 'шт'}
                };
            }
        }
        
        return {
            price: {price: 0},
            baseUnit: {isM2: false, title: 'шт'}
        };
    }
    /**
     * Отримує поточну категорію з роуту
     * @returns {string}
     */
    getCurrentCategoryFromRoute() {
        try {
            // Перевіряємо, чи є Vue router доступний
            if (typeof window !== 'undefined' && window.$nuxt) {
                const route = window.$nuxt.$route;
                if (route && route.params && route.params.slug) {
                    return route.params.slug;
                }
            }
            // Альтернативний спосіб - через URL
            const path = window.location.pathname;
            const pathParts = path.split('/');
            const katalogIndex = pathParts.findIndex(part => part === 'katalog');
            if (katalogIndex !== -1 && pathParts[katalogIndex + 1]) {
                return pathParts[katalogIndex + 1];
            }
            return '';
        } catch (e) {
            return '';
        }
    }
    /**
     * Отримує назву категорії з каталогу за slug
     * @param {string} slug - Slug категорії
     * @returns {string}
     */
    getCategoryNameFromCatalog(slug) {
        if (!slug) {
            return '';
        }
        // Список каталогів з футера (правильні slug з бази даних)
        const catalogMap = {
            'monolitnii-polikarbonat': 'Монолітний полікарбонат',
            'profilyovanii-polikarbonat': 'Профільований полікарбонат',
            'sotovii-polikarbonat': 'Сотовий полікарбонат',
            'vstavka-do-pokrivli-iz-sendvich-paneli': 'Вставка до покрівлі із сендвіч панелі',
            'svitloprozori-fasadni-systemy': 'Світлопрозорі фасадні системи',
            'svitloprozori-pokrivelni-systemy': 'Світлопрозорі покрівельні системи',
            'aksessuary': 'Аксесуари',
            'rishennya-dlya-avtomobiliv': 'Рішення для автомобілів',
            'agroteplitsi': 'Агротеплиці'
        };
        const categoryName = catalogMap[slug];
        if (categoryName) {
            return categoryName;
        } else {
            return slug;
        }
    }
    /**
     * Витягує категорію з DOM елемента
     * @param {HTMLElement} element - Елемент товару
     * @returns {Object}
     */
    extractCategoryFromDOM(element) {
        // Отримуємо категорію з роуту (каталогу)
        const routeCategory = this.getCurrentCategoryFromRoute();
        if (routeCategory) {
            const categoryName = this.getCategoryNameFromCatalog(routeCategory);
            if (categoryName) {
                return {name: categoryName};
            }
        }
        // Шукаємо елементи з категорією
        const categoryElements = element.querySelectorAll('[class*="category"], [class*="Category"]');
        for (const catEl of categoryElements) {
            const categoryText = catEl.textContent || catEl.innerText;
            if (categoryText.trim()) {
                return {
                    name: categoryText.trim()
                };
            }
        }
        // Фолбек: беремо категорію зі сторінки каталогу, якщо вона задана
        const pageCategory = document.querySelector('[data-current-category]')?.getAttribute('data-current-category');
        if (pageCategory && pageCategory.trim()) {
            return {name: pageCategory.trim()};
        }
        return {name: 'Невизначена категорія'};
    }
    /**
     * Витягує бренд з DOM елемента - копія з clickTracker
     * @param {HTMLElement} element - Елемент товару
     * @returns {Object}
     */
    extractBrandFromDOM(element) {
        // Шукаємо елементи з брендом
        const brandElements = element.querySelectorAll('[class*="brand"], [class*="Brand"]');
        for (const brandEl of brandElements) {
            const brandText = brandEl.textContent || brandEl.innerText;
            if (brandText.trim()) {
                return {
                    title: brandText.trim()
                };
            }
        }
        // Спробуємо витягнути бренд з title за евристикою (RODECA, BAUGLAS, BauGlas тощо)
        const titleText = element.getAttribute('data-product-title') || '';
        const parseBrandFromTitle = (title) => {
            const t = (title || '').trim();
            if (!t) return null;
            const commaIdx = t.indexOf(',');
            if (commaIdx > 0) {
                const beforeComma = t.slice(0, commaIdx).trim();
                if (beforeComma.length >= 2 && beforeComma.length <= 60) return beforeComma;
            }
            const capsMatch = t.match(/\b[A-ZА-ЯЇІЄҐ]{3,}\b/u);
            if (capsMatch && capsMatch[0]) return capsMatch[0];
            const camelMatch = t.match(/\b[A-Z][a-z]+[A-Z][a-zA-Z]+\b/);
            if (camelMatch && camelMatch[0]) return camelMatch[0];
            const firstWord = t.match(/\b[\p{L}]{3,}\b/u);
            if (firstWord && firstWord[0]) return firstWord[0];
            return null;
        };
        const brandFromTitle = parseBrandFromTitle(titleText);
        if (brandFromTitle) return {title: brandFromTitle};
        return {title: 'Невизначений бренд'};
    }
    /**
     * Витягує ID товару з картки
     * @param {Element} card - Картка товару
     * @returns {string|null}
     */
    extractIdFromCard(card) {
        // Шукаємо в data-атрибутах
        const dataId = card.getAttribute('data-product-id');
        if (dataId) return dataId;

        // Шукаємо в URL посилання
        const link = card.querySelector('a[href*="/product/"]');
        if (link) {
            const href = link.getAttribute('href');
            const match = href.match(/\/product\/([^\/]+)/);
            if (match) return match[1];
        }
        return null;
    }
    /**
     * Витягує назву товару з картки
     * @param {Element} card - Картка товару
     * @returns {string|null}
     */
    extractTitleFromCard(card) {
        // Різні селектори для пошуку назви товару
        const titleSelectors = [
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            '.title', '.product-title', '.card-title',
            '.product-name', '.item-title', '.name',
            '[data-title]', '[data-name]'
        ];
        
        for (const selector of titleSelectors) {
            const titleElement = card.querySelector(selector);
            if (titleElement) {
                const title = (titleElement.textContent || titleElement.innerText || '').trim();
                if (title && title.length > 2) {
                    return title;
                }
            }
        }
        // Якщо не знайшли через селектори, шукаємо в data-атрибутах
        const dataTitle = card.getAttribute('data-title') || card.getAttribute('data-name');
        if (dataTitle && dataTitle.trim().length > 2) {
            return dataTitle.trim();
        }
        return null;
    }
    /**
     * Витягує ціну з картки
     * @param {Element} card - Картка товару
     * @returns {number}
     */
    extractPriceFromCard(card) {
        // Шукаємо ціну в різних селекторах
        const priceSelectors = [
            '.price .price__value',
            '.price-value',
            '.product-price',
            '.card-price',
            '[data-price]',
            '.price',
            '.cost',
            '.amount'
        ];
        
        for (const selector of priceSelectors) {
            const priceElement = card.querySelector(selector);
            if (priceElement) {
                const priceText = priceElement.textContent || priceElement.innerText || '';
                const price = this.parsePriceFromText(priceText);
                if (price > 0) {
                    return price;
                }
            }
        }
        
        // Шукаємо в data-атрибутах
        const dataPrice = card.getAttribute('data-price');
        if (dataPrice) {
            const price = parseFloat(dataPrice);
            if (!isNaN(price) && price > 0) {
                return price;
            }
        }
        
        return 0;
    }
    
    /**
     * Парсить ціну з тексту
     * @param {string} text - Текст з ціною
     * @returns {number}
     */
    parsePriceFromText(text) {
        if (!text) return 0;
        
        // Видаляємо всі символи крім цифр, крапки та коми
        const cleanText = text.replace(/[^\d.,]/g, '');
        
        // Замінюємо кому на крапку для правильного парсингу
        const normalizedText = cleanText.replace(',', '.');
        
        const price = parseFloat(normalizedText);
        return isNaN(price) ? 0 : price;
    }
    /**
     * Витягує категорію з картки
     * @param {Element} card - Картка товару
     * @returns {string}
     */
    extractCategoryFromCard(card) {
        // Шукаємо елементи з категорією
        const categoryElements = card.querySelectorAll('[class*="category"], [class*="Category"]');
        for (const catEl of categoryElements) {
            const categoryText = catEl.textContent || catEl.innerText;
            if (categoryText.trim()) {
                return categoryText.trim();
            }
        }
        return 'Невизначена категорія';
    }
    /**
     * Витягує бренд з картки
     * @param {Element} card - Картка товару
     * @returns {string}
     */
    extractBrandFromCard(card) {
        // Шукаємо елементи з брендом
        const brandElements = card.querySelectorAll('[class*="brand"], [class*="Brand"]');

        for (const brandEl of brandElements) {
            const brandText = brandEl.textContent || brandEl.innerText;
            if (brandText.trim()) {
                return brandText.trim();
            }
        }
        return 'Невизначений бренд';
    }
    /**
     * Витягує кількість з картки або з поточної сторінки
     * @param {Element} card - Картка товару
     * @returns {number}
     */
    extractQuantityFromCard(card) {
        // Спочатку шукаємо в картці товару
        const quantityInput = card.querySelector('input[type="number"], .quantity input, .amount input');
        if (quantityInput) {
            const value = parseInt(quantityInput.value);
            return isNaN(value) ? 1 : value;
        }
        // Якщо не знайшли в картці, шукаємо на всій сторінці
        const globalQuantityInput = document.querySelector('input[type="number"], .quantity input, .amount input, input[placeholder*="кількість"], input[placeholder*="amount"]');
        if (globalQuantityInput) {
            const value = parseInt(globalQuantityInput.value);
            return isNaN(value) ? 1 : value;
        }
        // Шукаємо в тексті картки
        const quantityText = card.textContent || card.innerText;
        const quantityMatch = quantityText.match(/кількість[:\s]*(\d+)/i);
        if (quantityMatch) {
            return parseInt(quantityMatch[1]);
        }
        return 1; // За замовчуванням 1
    }
    /**
     * Отримує дані поточного товару (для сторінки товару)
     * @returns {Object|null}
     */
    getCurrentProductData() {
        // Спробуємо отримати з Vuex store
        if (window.Vue && window.Vue.prototype.$store) {
            const store = window.Vue.prototype.$store;
            const currentProduct = store.getters['catalog/currentProduct'];
            if (currentProduct) {
                // Використовуємо extractProductId для правильного витягування ID
                const rawId = this.extractProductId(currentProduct);
                // Переконуємося, що ID є числом
                const numericId = Number(rawId);
                const finalId = (!isNaN(numericId) && isFinite(numericId)) ? numericId : rawId;
                return {
                    id: finalId,
                    product_price_id: rawId || null, // Зберігаємо product_price_id якщо є
                    title: currentProduct.title,
                     productPrice: currentProduct.productPrice || {
                         price: {price: 0},
                         baseUnit: {isM2: false, title: 'шт'}
                     },
                     category: currentProduct.category || {name: 'Невизначена категорія'},
                    productBrand: currentProduct.productBrand || {title: 'Невизначений бренд'},
                    quantity: this.getQuantityFromPage()
                };
            }
        }
        // Спробуємо отримати з DOM
        return this.getProductDataFromDOM();
    }
    /**
     * Отримує кількість з поточної сторінки
     * @returns {number}
     */
    getQuantityFromPage() {
        // Шукаємо всі number input елементи та вибираємо найбільший значення
        const numberInputs = document.querySelectorAll('input[type="number"]');
        let maxQuantity = 1;
        let selectedInput = null;
        numberInputs.forEach((input) => {
            const value = parseInt(input.value);
            if (!isNaN(value) && value > 0) {
                // Пріоритет: елементи з placeholder, що містить цифри (кількість)
                if (input.placeholder && /^\d+$/.test(input.placeholder)) {
                    maxQuantity = value;
                    selectedInput = input;
                } else if (value > maxQuantity) {
                    maxQuantity = value;
                    selectedInput = input;
                }
            }
        });
        if (selectedInput) {
            return maxQuantity;
        }
        // Якщо не знайшли number input, шукаємо інші селектори
        const fallbackSelectors = [
            '.quantity input',
            '.amount input',
            'input[placeholder*="кількість"]',
            'input[placeholder*="amount"]',
            'input[name*="quantity"]',
            'input[name*="amount"]',
            'input[id*="quantity"]',
            'input[id*="amount"]',
            '.product-amount input',
            '.product-quantity input'
        ];
        for (const selector of fallbackSelectors) {
            const quantityInput = document.querySelector(selector);
            if (quantityInput) {
                const value = parseInt(quantityInput.value);
                if (!isNaN(value) && value > 0) {
                    return value;
                }
            }
        }
        return 1;
    }
    /**
     * Отримує дані товару з DOM
     * @returns {Object|null}
     */
    getProductDataFromDOM() {
        // Спочатку спробуємо знайти числовий ID в data-атрибутах
        const productCard = document.querySelector('[data-product-id]');
        if (productCard) {
            const dataProductId = productCard.getAttribute('data-product-id');
            // Якщо це числовий ID, використовуємо його
            const numericId = Number(dataProductId);
            if (!isNaN(numericId) && isFinite(numericId)) {
                const titleElement = document.querySelector('h1, h2, .card h2');
                const title = titleElement ? (titleElement.textContent || titleElement.innerText) : 'Товар';
                const brandFromTitle = this.extractBrandFromTitle(title);
                 return {
                     id: numericId,
                     title: title.trim(),
                     productPrice: {price: {price: 0}, baseUnit: {isM2: false, title: 'шт'}},
                     category: {name: 'Невизначена категорія'},
                     productBrand: {title: brandFromTitle || 'Невизначений бренд'},
                     quantity: this.getQuantityFromPage()
                 };
            }
        }
        // Фолбек: якщо не знайшли числовий ID, використовуємо slug
        const titleElement = document.querySelector('h1, h2, .card h2');
        if (!titleElement) {
            return null;
        }
        const title = titleElement.textContent || titleElement.innerText;
        const slug = window.location.pathname.match(/\/product\/([^\/]+)/);
        const productId = slug ? slug[1] : 'unknown';
        const brandFromTitle = this.extractBrandFromTitle(title);
         return {
             id: productId,
             title: title.trim(),
             productPrice: {price: {price: 0}, baseUnit: {isM2: false, title: 'шт'}},
                     category: {name: 'Невизначена категорія'},
             productBrand: {title: brandFromTitle || 'Невизначений бренд'},
             quantity: this.getQuantityFromPage()
         };
    }
    /**
     * Форматує дані товару для Google Analytics
     * @param {Object} productData - Дані товару
     * @returns {Object}
     */
    formatForAnalytics(productData) {
        const productId = this.extractProductId(productData);
        const normalizedId = this.normalizeId(productId);
        const price = this.extractPrice(productData);
        
        // Витягуємо бренд
        let itemBrand = '';
        if (productData.productBrand && productData.productBrand.title) {
            itemBrand = productData.productBrand.title;
        } else if (productData.title) {
            itemBrand = this.extractBrandFromTitle(productData.title);
        }
        
        // Визначаємо категорію з пріоритетом:
        // 1. З роуту (каталогу) - найвищий пріоритет
        // 2. Збережена в sessionStorage (з попереднього кліку)
        // 3. З даних товару (name або title)
        // 4. Фолбек
        let finalCategory = '';
        // Спочатку спробуємо отримати з роуту (каталогу)
        const routeCategory = this.getCurrentCategoryFromRoute();
        if (routeCategory) {
            const categoryName = this.getCategoryNameFromCatalog(routeCategory);
            if (categoryName) {
                finalCategory = categoryName;
            }
        }
        // Якщо не знайшли в роуті, спробуємо sessionStorage
        if (!finalCategory) {
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    finalCategory = sessionStorage.getItem('ga_last_item_category') || '';
                    if (finalCategory) {
                    }
                }
            } catch (e) { /* no-op */ }
        }
        // Якщо все ще не знайшли, спробуємо з даних товару
        if (!finalCategory && productData.category) {
            finalCategory = productData.category.name || productData.category.title || '';
            if (finalCategory) {
            }
        }
        if (!finalCategory) {
            finalCategory = 'Невизначена категорія';
        }
        
        const itemCategory = finalCategory;
        
        return {
            item_name: productData.title || '',
            item_id: normalizedId,
            price: price,
            currency: 'UAH',
            item_brand: itemBrand,
            item_category: itemCategory,
            quantity: productData.quantity || 1
        };
    }
    /**
     * Витягує бренд з заголовка товару
     * @param {string} title - Заголовок товару
     * @returns {string}
     */
    extractBrandFromTitle(title) {
        if (!title) return '';
        const parseBrandFromTitle = (title) => {
            const t = (title || '').trim();
            if (!t) return '';
            // Пріоритет 1: Шукаємо відомі бренди з модифікаторами
            const knownBrands = ['BauGlas', 'RODECA', 'BAUGLAS'];
            for (const brand of knownBrands) {
                // Спочатку шукаємо бренд з можливими модифікаторами
                const brandWithModifier = t.match(new RegExp(`\\b${brand}\\s+([A-Z][A-Za-z]*(?:\\s+[A-Z][A-Za-z]*)*)\\b`, 'i'));
                if (brandWithModifier && brandWithModifier[0]) {
                    return brandWithModifier[0];
                }
                // Якщо не знайшли з модифікатором, шукаємо просто бренд
                const brandMatch = t.match(new RegExp(`\\b${brand}\\b`, 'i'));
                if (brandMatch && brandMatch[0]) {
                    return brandMatch[0];
                }
            }
            // Додатковий пошук: шукаємо "BauGlas FSX Professional" як окремий випадок
            const bauGlasFSXProfessionalMatch = t.match(/\bBauGlas\s+FSX\s+Professional\b/i);
            if (bauGlasFSXProfessionalMatch && bauGlasFSXProfessionalMatch[0]) {
                return bauGlasFSXProfessionalMatch[0];
            }
            // Додатковий пошук: шукаємо "BauGlas FSX" як окремий випадок
            const bauGlasFSXMatch = t.match(/\bBauGlas\s+FSX\b/i);
            if (bauGlasFSXMatch && bauGlasFSXMatch[0]) {
                return bauGlasFSXMatch[0];
            }
            // Пріоритет 2: Шукаємо перед комою
            const commaIdx = t.indexOf(',');
            if (commaIdx > 0) {
                const beforeComma = t.slice(0, commaIdx).trim();
                if (beforeComma.length >= 2 && beforeComma.length <= 60) {
                    return beforeComma;
                }
            }
            // Пріоритет 3: Шукаємо CamelCase (BauGlas, BauGlas FSX)
            const camelMatch = t.match(/\b[A-Z][a-z]+[A-Z][a-zA-Z]+(?:\s+[A-Z]+)*\b/);
            if (camelMatch && camelMatch[0]) {
                return camelMatch[0];
            }
            // Пріоритет 4: Шукаємо слова з великих літер (RODECA, BAUGLAS, FSX)
            const capsMatch = t.match(/\b[A-ZА-ЯЇІЄҐ]{3,}\b/u);
            if (capsMatch && capsMatch[0]) {
                return capsMatch[0];
            }
            // Пріоритет 5: Беремо перше слово з 3+ літер
            const firstWord = t.match(/\b[\p{L}]{3,}\b/u);
            if (firstWord && firstWord[0]) {
                return firstWord[0];
            }
            return '';
        };
        return parseBrandFromTitle(title);
    }
    /**
     * Витягує ціну з даних товару
     * @param {Object} productData - Дані товару
     * @returns {number}
     */
    extractPrice(productData) {
        if (!productData || !productData.productPrice) {
            return 0;
        }
        
        // Перевіряємо структуру ціни
        if (productData.productPrice.price && typeof productData.productPrice.price.price === 'number') {
            return productData.productPrice.price.price;
        }
        
        // Альтернативна структура
        if (typeof productData.productPrice.price === 'number') {
            return productData.productPrice.price;
        }
        
        // Якщо ціна в основному об'єкті
        if (typeof productData.price === 'number') {
            return productData.price;
        }
        
        return 0;
    }
    /**
     * Витягує правильний ID товару з пріоритетом product_price_id
     * @param {Object} productData - Дані товару
     * @returns {any} ID товару
     */
    extractProductId(productData) {
        if (!productData) {
            return '';
        }
        
        // Пріоритет 1: productPrice.price.productPriceId (camelCase - основне місце з API)
        const priceProductPriceId = productData.productPrice?.price?.productPriceId;
        if (priceProductPriceId !== null && priceProductPriceId !== undefined) {
            return priceProductPriceId;
        }
        
        // Пріоритет 2: productPrice.priceOld.productPriceId (camelCase - якщо є знижка)
        const priceOldProductPriceId = productData.productPrice?.priceOld?.productPriceId;
        if (priceOldProductPriceId !== null && priceOldProductPriceId !== undefined) {
            return priceOldProductPriceId;
        }
        
        // Пріоритет 3: productPrice.price.product_price_id (snake_case fallback)
        const snakeCasePriceId = productData.productPrice?.price?.product_price_id;
        if (snakeCasePriceId !== null && snakeCasePriceId !== undefined) {
            return snakeCasePriceId;
        }
        
        // Пріоритет 4: productPrice.price_old.product_price_id (snake_case fallback)
        const snakeCasePriceOldId = productData.productPrice?.price_old?.product_price_id;
        if (snakeCasePriceOldId !== null && snakeCasePriceOldId !== undefined) {
            return snakeCasePriceOldId;
        }
        
        // Пріоритет 5: product_price_id на верхньому рівні об'єкта (fallback)
        const topLevelProductPriceId = productData.product_price_id;
        if (topLevelProductPriceId !== null && topLevelProductPriceId !== undefined) {
            return topLevelProductPriceId;
        }
        
        // Якщо product_price_id не знайдено - повертаємо порожній рядок (порожня колонка)
        return '';
    }
    /**
     * Нормалізує ID до числа, якщо можливо, або повертає рядок/порожнє значення
     */
    normalizeId(id) {
        if (id === null || id === undefined) return '';
        const num = Number(id);
        if (!Number.isNaN(num) && Number.isFinite(num)) return num;
        return ('' + id);
    }
    /**
     * Валідує дані товару перед відправкою
     * @param {Object} formattedProduct - Відформатовані дані для Analytics
     * @param {Object} originalProduct - Оригінальні дані товару
     * @returns {Object} - {isValid: boolean, errors: string[]}
     */
    validateProductData(formattedProduct, originalProduct) {
        const errors = [];
        // Перевіряємо ID товару
        if (!formattedProduct.item_id || formattedProduct.item_id === '' || formattedProduct.item_id === 0) {
            errors.push('Відсутній або невалідний ID товару');
        }
        // Перевіряємо назву товару
        if (!formattedProduct.item_name || formattedProduct.item_name.trim() === '') {
            errors.push('Відсутня назва товару');
        } else if (formattedProduct.item_name.length < 3) {
            errors.push(' Назва товару занадто коротка (менше 3 символів)');
        }
        // TODO: Implement price validation logic
        // Перевіряємо кількість
        if (!formattedProduct.quantity || formattedProduct.quantity <= 0) {
            errors.push('Невалідна кількість товару');
        } else if (formattedProduct.quantity > 1000) {
            errors.push('Кількість товару занадто велика (більше 1000)');
        }
        // Перевіряємо валюту
        if (!formattedProduct.currency || formattedProduct.currency !== 'UAH') {
            errors.push('Невалідна валюта (має бути UAH)');
        }
        // Перевіряємо бренд (менш строго)
        if (!formattedProduct.item_brand || formattedProduct.item_brand.trim() === '') {
            console.warn('Missing product brand, using "Unknown Brand"');
            formattedProduct.item_brand = 'Невизначений бренд';
        }
        // Перевіряємо категорію (менш строго)
        if (!formattedProduct.item_category || formattedProduct.item_category.trim() === '') {
            console.warn('Missing product category, using "Unknown Category"');
            formattedProduct.item_category = 'Невизначена категорія';
        }
        // Додаткові перевірки для оригінальних даних
        if (originalProduct) {
            const productId = this.extractProductId(originalProduct);
            if (!productId) {
                errors.push('Відсутній ID в оригінальних даних');
            }
            if (!originalProduct.title) {
                errors.push('Відсутня назва в оригінальних даних');
            }
            if (!originalProduct.productPrice) {
                errors.push('Відсутні дані ціни в оригінальних даних');
            }
        }
        const isValid = errors.length === 0;
        return {
            isValid: isValid,
            errors: errors
        };
    }
    /**
     * Відправляє подію add_to_cart в dataLayer
     * @param {Object} productData - Дані товару
     */
    sendAddToCartEvent(productData) {
        if (!window.dataLayer) {
            return;
        }
        // Перевіряємо глобальне дублювання
        const productId = this.extractProductId(productData);
        const normalizedId = this.normalizeId(productId);
        const now = Date.now();
        // Перевіряємо, чи не відправляли вже цей товар за останню секунду
        const lastSentAt = this.globalTracker.lastSentAt.get(normalizedId);
        if (lastSentAt && now - lastSentAt < 1000) {
            return;
        }
        // Убираем проверку через eventSignature, так как пользователь может добавлять один товар несколько раз
        // Оставляем только временную проверку дебаунса выше
        // Очищуємо попередні дані ecommerce
        window.dataLayer.push({ecommerce: null});
        // Форматуємо дані товару
        const formattedProduct = this.formatForAnalytics(productData);
        
        // Валідація даних перед відправкою
        const validationResult = this.validateProductData(formattedProduct, productData);
        if (!validationResult.isValid) {
            return;
        }

        // Відправляємо подію
        const eventData = {
            event: 'add_to_cart',
            ecommerce: {
                items: [formattedProduct]
            }
        };
        
        window.dataLayer.push(eventData);
        this.globalTracker.lastSentAt.set(normalizedId, now);
    }
    /**
     * Відстежує додавання товару вручну (для використання в компонентах)
     * @param {Object} productData - Дані товару
     * @param {number} quantity - Кількість товару
     */
    trackAddToCartManual(productData, quantity = 1) {
        const productId = this.extractProductId(productData);
        if (!productData || !productId) {
            return;
        }
        // Нормалізований ID для контролю дублікатів
        const normalizedId = this.normalizeId(productId);
        // Убираем блокировку повторного добавления товара в рамках сессии
        // Пользователь может добавлять один и тот же товар несколько раз
        // Дебаунс за часом: не частіше ніж раз на 1000мс для одного товару
        const now = Date.now();
        const lastAt = this.lastSentAtByProductId.get(normalizedId);
        if (lastAt && now - lastAt < 1000) {
            return;
        }
        this.lastSentAtByProductId.set(normalizedId, now);
        // Убираем добавление товара в Set, чтобы разрешить повторные добавления
        const dataWithQuantity = {...productData, quantity};
        this.sendAddToCartEvent(dataWithQuantity);
    }
    /**
     * Очищає відстеження для нової сторінки
     */
    reset() {
        this.addedProducts.clear();
    }
    /**
     * Робить цей екземпляр активним
     */
    makeActive() {
        if (this.globalTracker) {
            this.globalTracker.activeInstance = this.instanceId;
            this.init();
        }
    }
    /**
     * Зупиняє відстеження
     */
    destroy() {
        // Видаляємо слухачі подій
        document.removeEventListener('click', this.handleClick, true);
        // Видаляємо екземпляр з глобального реєстру
        if (this.globalTracker && this.globalTracker.instances) {
            this.globalTracker.instances.delete(this.instanceId);
            // Якщо це був активний екземпляр, переключаємося на інший
            if (this.globalTracker.activeInstance === this.instanceId) {
                const remainingInstances = Array.from(this.globalTracker.instances);
                if (remainingInstances.length > 0) {
                    this.globalTracker.activeInstance = remainingInstances[0];
                } else {
                    this.globalTracker.activeInstance = null;
                }
            }
        }
        this.reset();
    }
    /**
     * Статичний метод для отримання активного екземпляра
     */
    static getActiveInstance() {
        if (window.__globalAddToCartTracker && window.__globalAddToCartTracker.activeInstance) {
            return window.__globalAddToCartTracker.activeInstance;
        }
        return null;
    }
    /**
     * Статичний метод для очищення всіх екземплярів
     */
    static clearAllInstances() {
        if (window.__globalAddToCartTracker) {
            window.__globalAddToCartTracker.instances.clear();
            window.__globalAddToCartTracker.activeInstance = null;
            window.__globalAddToCartTracker.sentEvents.clear();
            window.__globalAddToCartTracker.lastSentAt.clear();
        }
    }
}
// Експортуємо для використання
export default ProductAddToCartTracker;